import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { api } from '../lib/api';

interface CronRunDetail {
  id: string;
  name: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: 'running' | 'success' | 'failure';
  summary: string;
  data: unknown;
  error: string | null;
}

function fmtDur(ms: number | null) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function StatusPill({ s }: { s: CronRunDetail['status'] }) {
  const palette =
    s === 'success'
      ? { bg: 'color-mix(in oklab, var(--accent) 12%, transparent)', fg: 'var(--accent)' }
      : s === 'failure'
      ? { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' }
      : { bg: 'var(--surface-2)', fg: 'var(--text-2)' };
  return (
    <span
      className="mono"
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      }}
    >
      {s}
    </span>
  );
}

function Stat({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{ marginTop: 6, fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

export default function CronRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery<CronRunDetail | { error: string }>({
    queryKey: ['admin', 'cron-run', id],
    queryFn: () => api(`/api/admin/cron-runs/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader kicker="OBSERVABILIDAD" title="Cargando ejecución…" />
      </div>
    );
  }

  if (!data || 'error' in data) {
    return (
      <div>
        <PageHeader kicker="OBSERVABILIDAD" title="Ejecución no encontrada" />
        <Link to="/cron-runs" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          ← Volver a la lista
        </Link>
      </div>
    );
  }

  const run = data;
  const dataJson = run.data != null ? JSON.stringify(run.data, null, 2) : null;

  return (
    <div>
      <PageHeader
        kicker={`CRON · ${run.name.toUpperCase()}`}
        title={run.summary || '(sin resumen)'}
        sub={`${new Date(run.startedAt).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'medium' })}${run.finishedAt ? ' → ' + new Date(run.finishedAt).toLocaleTimeString('es-MX') : ' (en curso)'}`}
        action={
          <Link
            to="/cron-runs"
            style={{
              background: 'transparent',
              border: '1px solid var(--border-2)',
              color: 'var(--text-2)',
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 12,
              textDecoration: 'none',
              fontFamily: 'inherit',
            }}
          >
            ← Volver
          </Link>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Stat label="ESTADO" value={<StatusPill s={run.status} />} />
        <Stat label="DURACIÓN" value={fmtDur(run.durationMs)} mono />
        <Stat label="CRON" value={run.name} mono />
        <Stat label="ID" value={run.id} mono />
      </div>

      {run.error && (
        <div style={{ marginBottom: 20, padding: 16, background: 'color-mix(in oklab, var(--danger) 8%, var(--surface))', border: '1px solid var(--danger)', borderRadius: 12 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--danger)', letterSpacing: '0.12em', marginBottom: 6 }}>ERROR</div>
          <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'JetBrains Mono, monospace' }}>{run.error}</pre>
        </div>
      )}

      {dataJson && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>PAYLOAD (data)</div>
          </div>
          <pre style={{ margin: 0, padding: 16, fontSize: 12, lineHeight: 1.5, fontFamily: 'JetBrains Mono, monospace', overflow: 'auto', maxHeight: 600, color: 'var(--text-2)' }}>
            {dataJson}
          </pre>
        </div>
      )}

      {!dataJson && !run.error && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
          Esta ejecución no produjo payload adicional.
        </div>
      )}
    </div>
  );
}
